// src/services/formService.js
import axiosInstance from '../utils/axios';
import { isDeclarationSection } from '../utils/undertaking';
import {
  getCategoryField, getCategoryOptions,
  normalizeRepeatableRows, resolveApplicantCategory,
} from '../utils/applicantCategory';
import { activeBranchFields, branchGroupLabel } from '../utils/conditionalBranch';
import { withNestedLabels } from '../utils/labelNumbering';
import { withBlockRules, toggleOpenValue } from '../utils/blockVisibility';
import {
  applicantNameKeys, buildSignatories, ownDeclarationSignature,
  isDeclarationNameField, declarationNameFor,
} from '../utils/signatories';
import { collectRepeatableDefs, resolveRepeatableRows } from '../utils/repeatableData';
import {
  checkboxStatements, isStatementAcknowledged, declarationAccepted, resolveUndertakings,
  declSignatoryBlankKey,
} from '../utils/declaration';

const FORMS_STORAGE_KEY = 'nba_saved_forms';

const tryParseJson = raw => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== 'string') return raw;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try { return JSON.parse(trimmed); } catch { return raw; }
};

const parseTemplateDataObject = input => {
  const parsed = tryParseJson(input);
  if (!parsed || typeof parsed !== 'object') return null;

  // Some environments return { responseObject: "{...}" } for templateData payload.
  if (parsed.responseObject && !parsed.ALL) {
    const nested = tryParseJson(parsed.responseObject);
    if (nested && typeof nested === 'object') return parseTemplateDataObject(nested) || nested;
  }

  // Handle double-encoded ALL value: { ALL: "{...}" }
  if (typeof parsed.ALL === 'string') {
    const allParsed = tryParseJson(parsed.ALL);
    if (allParsed && typeof allParsed === 'object') {
      return { ...parsed, ALL: allParsed };
    }
  }

  return parsed;
};

const unwrapApiPayload = raw => {
  let current = tryParseJson(raw);
  for (let i = 0; i < 6; i += 1) {
    if (!current || typeof current !== 'object') break;
    const next = current.responseObject ?? current.data ?? current.result ?? current.items ?? null;
    if (next === null || next === undefined) break;
    const parsedNext = tryParseJson(next);
    if (parsedNext === current) break;
    current = parsedNext;
  }
  return current;
};

/* ─────────────────────────────────────────────────────────
   LOCAL FORM CACHE (localStorage)
───────────────────────────────────────────────────────── */
export const saveForm = form => {
  try {
    if (!form || !form.id) return false;
    const saved = getSavedForms();
    const idx   = saved.findIndex(f => f.id === form.id);
    if (idx >= 0) saved[idx] = form; else saved.push(form);
    const unique = saved.reduce((acc, cur) => { if (!acc.some(f => f.id === cur.id)) acc.push(cur); return acc; }, []);
    localStorage.setItem(FORMS_STORAGE_KEY, JSON.stringify(unique));
    return true;
  } catch { return false; }
};

export const getSavedForms = () => {
  try { return JSON.parse(localStorage.getItem(FORMS_STORAGE_KEY) || '[]'); }
  catch { return []; }
};

export const deleteForm = formId => {
  try {
    const updated = getSavedForms().filter(f => f.id !== formId);
    localStorage.setItem(FORMS_STORAGE_KEY, JSON.stringify(updated));
    return true;
  } catch { return false; }
};

/* ─────────────────────────────────────────────────────────
   REMOTE FORM LIST & SINGLE FORM
───────────────────────────────────────────────────────── */
export const fetchAllForms = async () => {
  try {
    const res = await axiosInstance.get('/undp/secured/forms/getAll', { headers: { 'Accept-Language': 'en-IN' } });
    return res?.data?.responseObject || [];
  } catch { return []; }
};

export const fetchForm = async id => {
  try {
    const res = await axiosInstance.get(`/undp/secured/forms/view/${id}`, { headers: { 'Accept-Language': 'en-IN' } });
    // Handle 204 or any 2xx — axios may return data as string for 204
    const raw = res?.data;
    if (res?.status === 204 || !raw) return {};
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (!trimmed) return {};
      try { return JSON.parse(trimmed); } catch { return {}; }
    }
    return raw;
  } catch { return {}; }
};

/**
 * Fetch applicationFee payment details for a given form ID.
 * Handles 204 responses and multiple response structures.
 * Returns { paymentDetails: [...], formName, formDescription } or null.
 */
export const fetchFormFee = async (formId) => {
  if (!formId) return null;
  try {
    const res = await axiosInstance.get(`/undp/secured/forms/view/${formId}`, { headers: { 'Accept-Language': 'en-IN' } });
    // The API may return 204 with body — handle string or object response
    let formObj = null;
    const raw = res?.data;
    if (res?.status === 204 || !raw) return null;
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (!trimmed) return null;
      try { formObj = JSON.parse(trimmed); } catch { return null; }
    } else if (typeof raw === 'object') {
      formObj = raw;
    }
    if (!formObj) return null;

    // Extract the actual form object (could be nested in responseObject or responseList)
    const obj = formObj.responseObject || formObj.responseList?.[0] || formObj.data || formObj;

    // Parse applicationFee JSON string
    let feeStr = obj?.applicationFee;
    if (!feeStr && Array.isArray(formObj.responseList)) {
      feeStr = formObj.responseList[0]?.applicationFee;
    }

    let paymentDetails = null;
    if (feeStr) {
      try {
        const parsed = typeof feeStr === 'string' ? JSON.parse(feeStr) : feeStr;
        if (parsed?.paymentDetails) paymentDetails = parsed.paymentDetails;
        else if (Array.isArray(parsed) && parsed[0]?.applicantType) paymentDetails = parsed;
        else if (parsed?.applicationFee?.paymentDetails) paymentDetails = parsed.applicationFee.paymentDetails;
      } catch { /* parsing failed */ }
    }

    return {
      paymentDetails,
      formName: obj?.formName || obj?.name || '',
      formDescription: obj?.formDescription || obj?.description || '',
    };
  } catch { return null; }
};

/* ─────────────────────────────────────────────────────────
   APPLICATION DATA
───────────────────────────────────────────────────────── */
export const getApplicationData = async applicationId => {
  if (!applicationId) return null;
  try {
    const res = await axiosInstance.get(
      `/undp/secured/applicationData/view/${encodeURIComponent(applicationId)}`,
      { headers: { 'Accept-Language': 'en-IN', 'Cache-Control': 'no-cache' } }
    );
    if (res?.status === 204 || !res?.data) return null;
    const unwrapped = unwrapApiPayload(res.data);
    return unwrapped || null;
  } catch { return null; }
};

/* ─────────────────────────────────────────────────────────
   SUBMISSION DATE  ·  single source of truth
───────────────────────────────────────────────────────── */
/* An application only reaches the department once its fee is confirmed, so the
   instant the backend stamps `paymentCompletedAt` IS the submission date. That
   is what every "Submitted on" label must print — the creation date it used to
   fall back to is when the applicant STARTED the draft, which for a form filled
   over several days is simply a different (and wrong) date.

   Tried in order, first non-empty value across ALL sources wins per tier:
     1. paymentCompletedAt — the authoritative field (list API carries it).
     2. the same instant read off the transaction, for callers that only hold a
        payment payload: the /view/ endpoint does NOT return the field above, so
        without this tier the preview would silently fall through to tier 3.
     3. workflow/creation stamps — pre-payment-tracking records, and fee-exempt
        forms (Form 10) which never have a payment at all. Keeps the label from
        rendering empty rather than being a preferred answer. */
const SUBMITTED_ON_KEYS = [
  'paymentCompletedAt', 'paymentCompletedOn',
  'transactionDate', 'paymentDate', 'paidAt',
  'submittedOn', 'createdOn', 'createdAt',
];

/* Depending on the endpoint, these fields arrive either as a string or as the
   [y, mo, d, h, mi, s] array Java LocalDateTime serialises to (see
   parseWorkflowStamp, defined below — this only runs at call time). Every
   consumer hands the result straight to a display formatter, and those all
   begin with String(v), which renders such an array as the literal
   "2026,8,5,18,55" — matching no date pattern, so it gets printed verbatim.
   Collapse the array to ISO here, at the one point every "Submitted on" label
   reads from, so no formatter has to know the shape exists.
   Strings pass through untouched: the list APIs return dd-MM-yyyy display
   stamps the formatters already parse correctly, and rewriting those would be
   a regression. */
const normalizeStamp = v => {
  if (typeof v === 'string') return v;
  const dt = parseWorkflowStamp(v);
  return dt ? dt.toISOString() : v;
};

/**
 * Resolve the "Submitted on" timestamp from whatever objects a caller holds.
 * Pass every candidate (dashboard row, /view/ submission, transaction, oldest
 * workflow entry) — order between them only breaks ties within the same tier.
 * @returns {string} timestamp as ISO or the backend's own display string, '' when
 *                   nothing carries one
 */
export const resolveSubmittedOn = (...sources) => {
  const objs = sources.filter(o => o && typeof o === 'object');
  for (const key of SUBMITTED_ON_KEYS) {
    for (const o of objs) {
      const v = o[key];
      if (v != null && v !== '') return normalizeStamp(v);
    }
  }
  return '';
};

/* ─────────────────────────────────────────────────────────
   NBA UNIQUE APPLICATION ID
───────────────────────────────────────────────────────── */
/**
 * The NBA application number (e.g. "INBA08202602147") is minted by the backend
 * when a payment completes, so it does not exist while the applicant is filling
 * the form — it appears on the record only once the fee is confirmed. Callers on
 * the payment-success path therefore poll briefly: the number is generated in
 * the same request that records the payment, which can land a moment after the
 * browser is redirected back from the gateway.
 *
 * @param {number|string} applicationId internal id
 * @returns {Promise<string>} the NBA number, or '' if not assigned yet
 */
export const getNbaApplicationId = async applicationId => {
  if (!applicationId) return '';
  const raw = await getApplicationData(applicationId);
  if (!raw) return '';
  const rows = Array.isArray(raw) ? raw : [raw];
  for (const row of rows) {
    const id = row?.nbaApplicationId || row?.nbaApplicationNumber || row?.applicationNo;
    if (id) return String(id);
  }
  return '';
};

/**
 * Fetch the workflow / review history of an application — the ordered list of
 * stages it has passed through, each with a timestamp.
 *   GET /undp/secured/workflow/tracker/{applicationId}
 * Returns an array: [{ id, currentStep, submittedOn, lastUpdated, formId }]
 */
export const getWorkflowTracker = async applicationId => {
  if (!applicationId) return [];
  // NOTE: errors are intentionally NOT swallowed here. A 404 / "No records found"
  // (or a 200 with success:false) must reach the caller so the UI can show a
  // proper "no records" state instead of fabricating a timeline from stale data.
  const res = await axiosInstance.get(
    `/undp/secured/workflow/tracker/${encodeURIComponent(applicationId)}`,
    { headers: { 'Accept-Language': 'en-IN', 'Cache-Control': 'no-cache' } }
  );
  if (res?.status === 204) return [];
  const data = res?.data;
  // Some backends return HTTP 200 with an explicit "no records" envelope.
  if (data && typeof data === 'object' && !Array.isArray(data) && data.success === false) {
    const e = new Error(data.message || 'No workflow records found');
    e.code = 'NO_RECORDS';
    throw e;
  }
  if (!data) return [];
  const ro = unwrapApiPayload(data);
  if (Array.isArray(ro)) return ro;
  return Array.isArray(data) ? data : [];
};

/* ─────────────────────────────────────────────────────────
   WORKFLOW REVIEW HISTORY → REJECTION DETAILS

   The tracker endpoint above is the ONLY source of "who acted on this
   application, when, and with what remark" — the application-list API returns
   just a status. Column names differ between the workflow tables (and across
   environments), so every field below is read through a candidate list with a
   last-resort key scan: a renamed backend column then costs one line of the
   dialog instead of blanking the whole thing.
───────────────────────────────────────────────────────── */

const BLANK_RE = /^(null|undefined|na|n\/a|nil|none|-|—)$/i;
const isMeaningful = s => !!s && !BLANK_RE.test(s);

/**
 * Java LocalDateTime reaches the UI either as an ISO string or as a
 * [y, mo, d, h, mi, s, ns] array, depending on the endpoint's serialiser. The
 * list APIs additionally use dd-MM-yyyy, which `new Date()` misreads as a US
 * date (or as invalid). Accept all three.
 */
export const parseWorkflowStamp = v => {
  if (v == null || v === '') return null;
  if (Array.isArray(v)) {
    const [y, mo, d, h = 0, mi = 0, s = 0] = v;
    if (!y || !mo || !d) return null;
    const dt = new Date(y, mo - 1, d, h, mi, s);
    return isNaN(dt.getTime()) ? null : dt;
  }
  const str = String(v).trim();
  if (!str) return null;
  const m = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/.exec(str);
  if (m) {
    const dt = new Date(+m[3], +m[2] - 1, +m[1], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0));
    return isNaN(dt.getTime()) ? null : dt;
  }
  const dt = new Date(str);
  return isNaN(dt.getTime()) ? null : dt;
};

/** A workflow timestamp as "28 Jul 2026, 14:32" — or the raw value if unparsable. */
export const formatWorkflowStamp = v => {
  const dt = parseWorkflowStamp(v);
  if (!dt) return v ? String(v) : '';
  return dt.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

/* Backend workflow role → the label an applicant should read. */
export const WORKFLOW_ROLE_LABELS = {
  NBA_ACCOUNT_OFFICER: 'Account Officer / DDO',
  NBA_SCRUTINY: 'NBA Scrutiny',
  NBA_REVIEW_HEAD_TECH: 'Review Head — Technical',
  NBA_REVIEW_HEAD_IPR: 'Review Head — IPR',
  NBA_REVIEW_HEAD_LEGAL: 'Review Head — Legal',
  NBA_REVIEW_MEMBERS_TECH_SCIENTIFIC: 'Review Members — Technical (Scientific)',
  NBA_REVIEW_MEMBER_TECH_LEGAL: 'Review Member — Technical (Legal)',
  NBA_REVIEW_MEMBERS_IPR_SCIENTIFIC: 'Review Members — IPR (Scientific)',
  NBA_REVIEW_MEMBER_IPR_LEGAL: 'Review Member — IPR (Legal)',
  NBA_REVIEW_MEMBERS_LEGAL: 'Review Members — Legal',
  NBA_REVIEW_MEMBERS_SCIENTIFIC_LEGAL: 'Review Members — Scientific / Legal',
  NBA_REVIEW_MEMBERS_SCIENTIFIC: 'Review Members — Scientific',
  NBA_REVIEW_MEMBER_MONITORING: 'Review Member — Monitoring',
  NBA_SECTIONAL_HEAD: 'Sectional Head',
  NBA_EXPERT_COMMITTEE: 'Expert Committee',
  NBA_AUTHORITY: 'Authority',
  NBA_SECRETARY: 'Secretary',
  NBA_CHAIRPERSON: 'Chairperson',
  NBA_ADJUDICATING_OFFICER: 'Adjudicating Officer',
  APPLICANT: 'Applicant',
};

export const workflowRoleLabel = raw => {
  const k = String(raw || '').trim().toUpperCase();
  if (!k) return '';
  if (WORKFLOW_ROLE_LABELS[k]) return WORKFLOW_ROLE_LABELS[k];
  return k.replace(/^NBA[_\s]*/, '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
};

// First candidate key holding a usable string. Object values (e.g. a nested
// user record) are flattened to their name/email.
const firstValue = (obj, keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v == null) continue;
    const s = typeof v === 'object'
      ? (v.name || v.fullName || v.userName || v.displayName || v.email || v.userEmailId || '')
      : v;
    const t = String(s).trim();
    if (isMeaningful(t)) return t;
  }
  return '';
};

// Last resort when the backend renames a column: any string-valued key whose
// NAME reads like the field we want. `reject` filters out ids and enum codes.
const scanValue = (obj, keyRe, reject) => {
  if (!obj || typeof obj !== 'object') return '';
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v !== 'string' || !keyRe.test(k)) continue;
    const t = v.trim();
    if (isMeaningful(t) && !(reject && reject(t))) return t;
  }
  return '';
};

const ACTOR_KEYS = [
  'actionByName', 'actionBy', 'rejectedByName', 'rejectedBy', 'approvedBy',
  'updatedByName', 'updatedBy', 'modifiedByName', 'modifiedBy', 'performedBy',
  'reviewerName', 'reviewedBy', 'officerName', 'employeeName', 'userFullName',
  'userName', 'createdBy', 'assignedTo', 'user',
];
const REMARK_KEYS = [
  'remarks', 'remark', 'rejectionRemarks', 'rejectionReason', 'rejectReason',
  'comments', 'comment', 'reason', 'reviewComments', 'reviewRemarks',
  'observation', 'observations', 'justification', 'feedback', 'note', 'notes',
  'description', 'message',
];
const DECISION_KEYS = [
  'action', 'actionTaken', 'decision', 'status', 'currentStatus',
  'workflowStatus', 'applicationStatus', 'stepStatus',
];
const STAMP_KEYS = [
  'lastUpdated', 'actionOn', 'actionDate', 'rejectedOn', 'updatedOn',
  'modifiedOn', 'createdOn', 'submittedOn',
];

// A bare numeric id is a worse answer than "—", so never show one as a person.
const isBareId = t => /^\d+$/.test(t);

const REJECT_RE = /REJECT|DECLIN/i;

/**
 * One tracker record → the fields the UI actually renders. `raw` is kept so a
 * caller can reach a field this normaliser doesn't know about.
 */
export const normalizeWorkflowEntry = (raw = {}) => {
  const step = String(raw.currentStep ?? raw.step ?? raw.workflowStep ?? raw.ownership ?? '').trim();
  const decision = firstValue(raw, DECISION_KEYS);
  const at = STAMP_KEYS.map(k => raw[k]).find(v => v != null && v !== '') ?? null;
  const by = firstValue(raw, ACTOR_KEYS.filter(k => !isBareId(String(raw[k] ?? ''))))
    || scanValue(raw, /(by|user|officer|employee|reviewer|assignee)/i, isBareId);
  const remarks = firstValue(raw, REMARK_KEYS)
    || scanValue(raw, /(remark|comment|reason|note|feedback|observ|justif)/i);

  return {
    id: raw.id ?? null,
    step,
    stepLabel: workflowRoleLabel(step) || step || '—',
    decision,
    by,
    at,
    atLabel: formatWorkflowStamp(at),
    remarks,
    submittedOn: raw.submittedOn ?? null,
    isRejection: REJECT_RE.test(`${step} ${decision}`),
    raw,
  };
};

/**
 * Full review trail of an application plus the entry that rejected it.
 *   → { entries: [oldest…newest], rejection, submittedOn }
 *
 * Propagates the tracker's errors (incl. the NO_RECORDS case) so the caller can
 * show a real "no review records" state rather than an empty timeline.
 */
export const getRejectionDetails = async applicationId => {
  const history = await getWorkflowTracker(applicationId);
  const entries = (Array.isArray(history) ? history : [])
    .filter(Boolean)
    .map(normalizeWorkflowEntry)
    .sort((a, b) => (a.id || 0) - (b.id || 0));   // chronological — oldest first

  // Prefer the newest entry that names the rejection outright. When the backend
  // records the rejection only as an application-level status change, the newest
  // entry is still where the application stopped moving — i.e. the deciding desk.
  const rejection = [...entries].reverse().find(e => e.isRejection)
    || entries[entries.length - 1]
    || null;

  return { entries, rejection, submittedOn: entries[0]?.submittedOn ?? null };
};

/**
 * Fetch payment / transaction details for an application (available only AFTER
 * payment — i.e. for Completed & Under-Review applications). Returns the single
 * transaction object (latest if the API returns a list) or null.
 *   GET /api/v1/transaction-details/application/{applicationId}
 */
export const getTransactionDetails = async applicationId => {
  if (!applicationId) return null;
  try {
    const res = await axiosInstance.get(
      `/api/v1/transaction-details/application/${encodeURIComponent(applicationId)}`,
      { headers: { 'Accept-Language': 'en-IN', 'Cache-Control': 'no-cache' } }
    );
    if (res?.status === 204 || !res?.data) return null;
    const ro = unwrapApiPayload(res.data);
    if (Array.isArray(ro)) return ro.find(Boolean) || null;
    return ro && typeof ro === 'object' ? ro : null;
  } catch { return null; }
};

/* A transaction worth printing: an object carrying at least one real value. An
   empty envelope ({}) means "nothing recorded yet", not "a payment with no
   details", and must never be mistaken for an answer. */
export const hasTransactionData = t =>
  !!t && typeof t === 'object' && !Array.isArray(t)
  && Object.values(t).some(v => v != null && String(v).trim() !== '');

/* The stored transaction is written server-side a beat AFTER the browser
   returns from the gateway, and getTransactionDetails cannot tell "not yet"
   from "never" — both are `null`. Every caller that prints a payment receipt
   therefore polls, the same way the NBA number is polled, instead of taking the
   first empty answer as final. One implementation so the preview and the
   printed receipt both wait the same way.

   @param {number|string} applicationId
   @param {{delays?: number[], signal?: AbortSignal, isCancelled?: () => boolean}} [opts]
   @returns {Promise<object|null>} */
export const TRANSACTION_POLL_DELAYS_MS = [0, 1500, 3000, 5000];
export const getTransactionDetailsWithRetry = async (applicationId, opts = {}) => {
  const { delays = TRANSACTION_POLL_DELAYS_MS, signal, isCancelled } = opts;
  const stopped = () => !!signal?.aborted || (typeof isCancelled === 'function' && isCancelled());
  for (let i = 0; i < delays.length; i += 1) {
    if (stopped()) return null;
    if (delays[i]) await new Promise(r => setTimeout(r, delays[i]));
    if (stopped()) return null;
    const t = await getTransactionDetails(applicationId);
    if (hasTransactionData(t)) return t;
  }
  return null;
};

/* ─────────────────────────────────────────────────────────
   DOCUMENT HELPERS
───────────────────────────────────────────────────────── */

/* ── The official copy of the application ─────────────────────────────────
   The backend generates the final application PDF once the application is
   submitted and stores it on the same application, alongside the applicant's
   own uploads. Two things follow from that:

     • it is NOT an attachment the applicant supplied, so the preview must never
       list it as one (extractDocuments / withoutGeneratedApplicationCopies);
     • the "Download PDF" button fetches it from its own endpoint
       (fetchApplicationPdf below), not from this documents list.

   Nothing on the document row says "generated" explicitly, so it is recognised
   by its identity. Its stored name is the application's own reference number
   (e.g. "IN8AE202602189.pdf"), or `Application-<id>.pdf` on older records, and
   it may sit in section 0. The reference number is the strongest signal: it is
   printed on the raw applicationData response itself, and nothing an applicant
   uploads before paying can carry the number minted at payment.

   The filename pattern is matched against THIS application's own id, never any
   "Application-<digits>.pdf" — an applicant's own upload (e.g. a re-uploaded
   copy of some other application's PDF used as address proof) can legitimately
   be named that way for a DIFFERENT id, and a blanket digit match was silently
   dropping it from the annexures. */
const baseName = p => String(p || '').split(/[\\/]/).pop().split('?')[0];
const stripExt = n => baseName(n).replace(/\.[a-z0-9]+$/i, '');
const isGeneratedSectionId = sid => sid === 0 || sid === '0';
/* The backend may also decorate the name it stores — a timestamp or uuid in
   front, a de-duplication suffix behind ("Application-123 (1).pdf",
   "IN8AE202602189_1726048812.pdf") — so both matches tolerate a separator-led
   prefix/suffix, while still requiring THIS application's own id or number. */
const generatedPdfNameRe = applicationId => {
  const id = Number(applicationId);
  return Number.isFinite(id) && id > 0
    ? new RegExp(`(?:^|[\\s._-])application[\\s_-]?${id}(?:[\\s._()-][^\\\\/]*)?\\.pdf$`, 'i')
    : null;
};
const SEP_RE = /^[\s._()-]/;
// The raw spelling — separators kept, so a decorated name can be told apart.
const stemMatchesRef = (stem, ref) =>
  (stem.startsWith(ref) && SEP_RE.test(stem.slice(ref.length))) ||
  (stem.endsWith(ref) && SEP_RE.test(stem.slice(-ref.length - 1, -ref.length)));
/* Letters and digits only. The backend writes the reference number into a
   FILENAME, and may drop the separators the record itself carries
   ("INBAE/2026/02189" on the row, "INBAE202602189.pdf" on disk) — comparing
   raw spellings missed exactly that. */
const alnum = s => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
const refList = appNo => {
  const out = [];
  (Array.isArray(appNo) ? appNo : [appNo]).forEach(r => {
    const raw = String(r ?? '').trim().toLowerCase();
    const flat = alnum(raw);
    // Short values are internal ids or noise, never a minted reference number.
    if (flat.length >= 6 && !out.some(o => o.flat === flat)) out.push({ raw, flat, source: String(r) });
  });
  return out;
};
const isPdfDoc = d => /\.pdf$/i.test(baseName(d?.fileName) || baseName(d?.filePath)) || /pdf/i.test(String(d?.fileType || ''));

/* The shape of the NBA number the backend mints on payment and may rename the
   generated copy to: a short letter prefix, then a long digit run
   ("INBA08202602147", "IN8AE202602189", "INBAE202602189"). */
const REF_NUMBER_STEM_RE = /^[a-z]{2}[a-z0-9]{0,8}\d{6,}$/i;

/* Temporary: log every generated-copy classification so a document that is
   wrongly kept or dropped can be traced to the rule that decided it. */
const DEBUG_GENERATED_COPY = true;
const logClassification = (stage, doc, result, applicationId, refs) => {
  if (!DEBUG_GENERATED_COPY) return;
  try {
    console.log(`[generated-copy] ${stage}`, {
      id: doc?.id, fileName: doc?.fileName, fileType: doc?.fileType, filePath: doc?.filePath, sectionId: doc?.sectionId,
      applicationId, refs: refs.map(r => r.source),
      isGeneratedApplicationPdf: result.generated, reason: result.reason, matched: result.matched || '',
    });
  } catch { /* logging must never break a download */ }
};

/**
 * Is `doc` the backend's generated copy of THIS application? Decided purely
 * from the document's identity — its section, id-bearing name, and the
 * application's reference numbers — never from its content or page count, so
 * it answers the same for a 1-page form and a 20-page one.
 *
 *   1. sectionId 0 — the application as a whole, not one form section;
 *   2. `Application-<this id>.pdf` — the name used on older records;
 *   3. a name that IS one of this application's reference numbers, compared
 *      letters-and-digits only — the backend's renamed copy;
 *   4. a reference number with a separator-led prefix/suffix
 *      ("INBAE202602189_1726048812.pdf") — unless a form field points at it;
 *   5. with `referenced` known: a PDF no field points at whose name is shaped
 *      like a reference number — covers a caller that has no reference number.
 *
 * A genuine upload is always pointed at by its field (`[file:<name>]`), which
 * is what keeps rules 4 and 5 from ever taking one; and nothing an applicant
 * uploads BEFORE paying can carry the number minted AT payment (rule 3).
 *
 * @returns {{generated:boolean, reason:string, matched?:string}}
 */
export const classifyGeneratedApplicationPdf = (doc, { sectionId, refs, applicationId, referenced } = {}) => {
  if (!doc) return { generated: false, reason: 'no document' };
  const sid = doc.sectionId ?? sectionId;
  if (isGeneratedSectionId(sid)) return { generated: true, reason: 'sectionId 0 (generated upload)', matched: String(sid) };

  const names = [baseName(doc.fileName), baseName(doc.filePath)].filter(Boolean);
  const nameRe = generatedPdfNameRe(applicationId);
  const byName = nameRe && names.find(n => nameRe.test(n));
  if (byName) return { generated: true, reason: 'Application-<applicationId>.pdf name', matched: byName };

  const refObjs = Array.isArray(refs) && refs.length && typeof refs[0] === 'object' && refs[0] && 'flat' in refs[0] ? refs : refList(refs);
  const stems = names.map(n => stripExt(n).toLowerCase());
  for (const ref of refObjs) {
    const hit = stems.find(stem => alnum(stem) === ref.flat);
    if (hit) return { generated: true, reason: 'name equals application reference number', matched: ref.source };
  }

  const isRef = !!referenced && (referenced.has(doc.fileName) || names.some(n => referenced.has(n)));
  if (!isRef) {
    for (const ref of refObjs) {
      const hit = stems.find(stem => stemMatchesRef(stem, ref.raw) || stemMatchesRef(stem, ref.flat));
      if (hit) return { generated: true, reason: 'name is reference number with prefix/suffix, not referenced by any field', matched: ref.source };
    }
    if (referenced && isPdfDoc(doc)) {
      const shaped = stems.find(stem => REF_NUMBER_STEM_RE.test(stem));
      if (shaped) return { generated: true, reason: 'reference-number-shaped PDF not referenced by any field', matched: shaped };
    }
  }
  return { generated: false, reason: isRef ? 'referenced by a form field' : 'no generated-copy signal' };
};

export const isGeneratedApplicationPdf = (doc, sectionId, appNo, applicationId) =>
  classifyGeneratedApplicationPdf(doc, { sectionId, refs: appNo, applicationId }).generated;

/* Reference numbers carried on any row of the raw applicationData response.
   Every row, not just the first — getNbaApplicationId reads them the same way. */
export const applicationRefsFromRaw = appDataRaw => {
  const rows = Array.isArray(appDataRaw) ? appDataRaw : [appDataRaw];
  const out = [];
  rows.forEach(r => {
    if (!r || typeof r !== 'object') return;
    out.push(r.nbaApplicationId, r.nbaApplicationNumber, r.applicationNo, r.appNo, r.referenceNo);
  });
  return out;
};

/* Every file name the filled form itself points at — `[file:<name>]` values
   and File objects still held in memory. An applicant's upload is always
   reachable from the field it was uploaded through; the portal's generated
   copy never is. Only those two unambiguous shapes count: a looser match on
   any `{ fileName }` object could pick up a copy of a document row and shield
   the generated file from exclusion. */
export const collectReferencedFileNames = (...sources) => {
  const names = new Set();
  const seen = new WeakSet();
  const walk = (v, depth) => {
    if (v == null || depth > 40) return;
    if (typeof v === 'string') {
      const m = v.match(/^\[file:(.+)\]$/);
      if (m) names.add(m[1]);
      return;
    }
    if (typeof v !== 'object' || seen.has(v)) return;
    seen.add(v);
    if (typeof File !== 'undefined' && v instanceof File) { names.add(v.name); return; }
    if (Array.isArray(v)) { v.forEach(x => walk(x, depth + 1)); return; }
    Object.keys(v).forEach(k => { if (k !== 'documents') walk(v[k], depth + 1); });
  };
  sources.forEach(s => walk(s, 0));
  return names;
};

/**
 * Drop the backend's generated copy of the application from a documents list
 * (see classifyGeneratedApplicationPdf), so the preview only ever shows what
 * the applicant uploaded. Pass every reference number the caller holds in
 * `refs` — the dashboard row's too, since the applicationData response does
 * not always carry them — and `referenced` (collectReferencedFileNames of the
 * filled pages) so genuine uploads are recognised by their field.
 * Returns the input array itself when nothing was removed.
 */
export const withoutGeneratedApplicationCopies = (documents, { applicationId, refs, referenced } = {}) => {
  const list = Array.isArray(documents) ? documents : [];
  const refObjs = refList(refs);
  const out = list.filter(d => {
    if (!d) return false;
    const result = classifyGeneratedApplicationPdf(d, { refs: refObjs, applicationId, referenced });
    logClassification('annexure filter', d, result, applicationId, refObjs);
    return !result.generated;
  });
  return out.length === list.length ? list : out;
};

/**
 * Extract documents array from the applicationData API response.
 * The response is an array of sections, each may contain a `documents` array.
 * The portal's own generated copy of the application is not one of them.
 * `applicationId` (when known) lets the exclusion match THIS application's
 * generated file precisely instead of any similarly-named upload.
 */
export const extractDocuments = (appDataRaw, applicationId, extraRefs = []) => {
  if (!appDataRaw) return [];
  const sections = Array.isArray(appDataRaw) ? appDataRaw : [appDataRaw];
  // The reference numbers are read from the response itself so the exclusion
  // works without every caller having to thread the number through separately.
  // That row does not always carry the number, though, so a caller that has it
  // (the dashboard row) passes it in as `extraRefs`.
  const refs = refList([...applicationRefsFromRaw(sections), ...(Array.isArray(extraRefs) ? extraRefs : [extraRefs])]);
  const docs = [];
  sections.forEach(sec => {
    if (Array.isArray(sec?.documents)) {
      sec.documents.forEach(d => {
        if (!d?.id) return;
        const result = classifyGeneratedApplicationPdf(d, { sectionId: sec?.sectionId, refs, applicationId });
        logClassification('extractDocuments', d, result, applicationId, refs);
        if (!result.generated) docs.push(d);
      });
    }
  });
  return docs;
};

/* ── The application PDF (generated by the backend) ────────────────────────
   GET /undp/secured/filesystem/application/{applicationId}/pdf streams the
   PDF the backend generated for the application — one request, no lookup. */
const applicationPdfUrl = applicationId =>
  `/undp/secured/filesystem/application/${encodeURIComponent(applicationId)}/pdf`;

/* A PDF can be several MB; axiosInstance's 30 s default is sized for JSON. */
const FILE_TIMEOUT_MS = 90000;

/* A fetched PDF is reused for a few minutes, so a second click opens instantly
   and a prefetch (see prefetchApplicationPdf) is not thrown away. Kept short
   because the backend may regenerate the document; capped so a long session
   viewing many applications does not hold every file in memory. */
const PDF_CACHE_TTL_MS = 5 * 60 * 1000;
const PDF_CACHE_MAX    = 4;
const pdfCache    = new Map();   // applicationId → { file, ts }
const pdfInFlight = new Map();   // applicationId → Promise

/* "filename*=UTF-8''…" or filename="…". Only readable if the server lists
   Content-Disposition in Access-Control-Expose-Headers; there is a fallback. */
const fileNameFromDisposition = cd => {
  const m = String(cd || '').match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
  if (!m) return '';
  try { return baseName(decodeURIComponent(m[1] || m[2] || '').trim()); } catch { return baseName(m[2] || ''); }
};

/* Every real PDF carries the "%PDF-" marker within its first 1 KB. An error
   page or JSON envelope answered with a 200 must never be shown as a broken
   document. */
const looksLikePdf = async blob => {
  try { return (await blob.slice(0, 1024).text()).includes('%PDF-'); }
  catch { return true; }   // can't inspect — let the viewer decide
};

const downloadApplicationPdf = async applicationId => {
  const res = await axiosInstance.get(applicationPdfUrl(applicationId), {
    responseType: 'blob',
    timeout: FILE_TIMEOUT_MS,
    headers: { Accept: 'application/pdf', 'Cache-Control': 'no-cache' },
    // 404/204 = "not generated yet", an answer rather than a failure. 401 is
    // deliberately NOT accepted here, so the interceptor can refresh the token.
    validateStatus: s => (s >= 200 && s < 300) || s === 404,
  });
  const data = res?.data;
  if (res.status === 404 || res.status === 204 || !data || !data.size) return null;
  if (/json|html/i.test(String(res.headers?.['content-type'] || '')) || !(await looksLikePdf(data))) {
    console.warn(`[application-pdf] ${applicationId}: server answered without a PDF`);
    return null;
  }
  return {
    // Typed explicitly so the browser shows the file inline instead of saving it.
    blob: data.type === 'application/pdf' ? data : new Blob([data], { type: 'application/pdf' }),
    fileName: fileNameFromDisposition(res.headers?.['content-disposition']) || `Application-${applicationId}.pdf`,
  };
};

/**
 * The PDF the backend generated for an application.
 *
 * Concurrent calls for the same application share one request, and a result
 * is reused for PDF_CACHE_TTL_MS. "Not ready" and failures are never cached,
 * so the next click asks again.
 *
 * @param {number|string} applicationId
 * @returns {Promise<{blob: Blob, fileName: string}|null>} null when the backend
 *        has not produced the document yet. Network/server failures throw, so
 *        the caller can tell "not ready" apart from "could not be reached".
 */
export const fetchApplicationPdf = async applicationId => {
  if (!applicationId) return null;
  const key = String(applicationId);

  const hit = pdfCache.get(key);
  if (hit && Date.now() - hit.ts < PDF_CACHE_TTL_MS) return hit.file;
  if (hit) pdfCache.delete(key);

  if (pdfInFlight.has(key)) return pdfInFlight.get(key);
  const job = downloadApplicationPdf(key)
    .then(file => {
      if (file) {
        pdfCache.set(key, { file, ts: Date.now() });
        while (pdfCache.size > PDF_CACHE_MAX) pdfCache.delete(pdfCache.keys().next().value);
      }
      return file;
    })
    .finally(() => { pdfInFlight.delete(key); });
  pdfInFlight.set(key, job);
  return job;
};

/** Start fetching ahead of a likely click (hover / focus). Never throws. */
export const prefetchApplicationPdf = applicationId => {
  if (!applicationId) return;
  fetchApplicationPdf(applicationId).catch(() => { /* the click will report it */ });
};

/* ─────────────────────────────────────────────────────────
   parseSectionsToFormData
   ─────────────────────────────────────────────────────────
   FIX: Correctly extracts from { ALL: { s1:{...}, s2:{...},
   "Applicant Details": [...], ... } } structure.

   The saved templateData from otherformssaveorupdate looks like:
   {
     "ALL": {
       "(1) Particulars of the Applicant": { "has_contact_person_in_india": "", ... },
       "(2) Details of the biological resources...": { "has_traditional_knowledge": "", ... },
       "Applicant Details": [{ "category_of_applicant": "Individual", ... }],
       "category_of_applicant": "Individual"   ← also at root of ALL
     }
   }

   We need to:
   1. Flatten section objects (scalar sections) into root
   2. Keep array sections (like "Applicant Details") as arrays
   3. Resolve category_of_applicant — the ALL root value (the applicant's own
      selection) first, and a repeatable row's read-only copy only as a
      section-checked last resort
───────────────────────────────────────────────────────── */
export const parseSectionsToFormData = sections => {
  if (!sections) return {};
  const arr = Array.isArray(sections) ? sections : [sections];
  const out = {};

  for (const sec of arr) {
    let td = parseTemplateDataObject(sec?.templateData);
    if (!td || typeof td !== 'object') continue;

    // Main case: { ALL: { ... } }
    if (td.ALL && typeof td.ALL === 'object') {
      const ALL = td.ALL;

      Object.entries(ALL).forEach(([sKey, sData]) => {
        // Skip all metadata keys
        if (sKey.startsWith('_')) return;
        if (Array.isArray(sData)) {
          // Array sections (repeatable rows) — keep as-is
          out[sKey] = sData;
        } else if (sData && typeof sData === 'object') {
          // Scalar section — merge all fields into root
          Object.entries(sData).forEach(([k, v]) => {
            out[k] = v;
          });
          // Also preserve the section-keyed object for category inference
          out[sKey] = sData;
        } else {
          // Direct scalar at ALL root
          out[sKey] = sData;
        }
      });

    } else {
      // Flat structure (no ALL wrapper) — merge everything except internal keys
      Object.entries(td).forEach(([k, v]) => {
        if (k.startsWith('_')) return;
        out[k] = v;
      });
    }
  }

  // Category of the applicant. The root value is the one the applicant actually
  // selected (and the one the fee was charged against), so it always wins; the
  // read-only copy on a repeatable row is consulted only when the root is truly
  // absent, and then only via the section-aware resolver.
  const catField = getCategoryField(null);
  if (!out[catField]) {
    const derived = resolveApplicantCategory(out, [], catField);
    if (derived) out[catField] = derived;
  }

  return out;
};

/* ─────────────────────────────────────────────────────────
   buildFilledPagesFromMetadata (FALLBACK)
   ─────────────────────────────────────────────────────────
   When the form template API returns 204/empty, reconstruct
   display pages using the _pageMap, _fieldLabels, and
   _sectionNames metadata embedded in the saved templateData.
───────────────────────────────────────────────────────── */
const buildFilledPagesFromMetadata = (allData, pageMap, fieldLabels, sectionNames, repeatableMeta) => {
  // pageMap: { sectionKey: { pageNumber, pageName } }
  // fieldLabels: { fieldKey: displayLabel }
  // sectionNames: { sectionKey: displayName }
  // repeatableMeta: { repeatableKey: { sectionId, pageName, pageNumber } }

  // Group sections by page number
  const pageGroups = {};
  Object.entries(pageMap).forEach(([sectionKey, info]) => {
    const pn = info.pageNumber || 1;
    if (!pageGroups[pn]) pageGroups[pn] = { pageName: info.pageName || `Page ${pn}`, sections: [] };
    pageGroups[pn].sections.push(sectionKey);
  });

  // Build flat data lookup
  const flatData = {};
  Object.entries(allData).forEach(([key, val]) => {
    if (key.startsWith('_')) return;
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      Object.assign(flatData, val);
    } else if (!Array.isArray(val)) {
      flatData[key] = val;
    }
  });
  Object.entries(allData).forEach(([key, val]) => {
    if (Array.isArray(val)) flatData[key] = val;
  });

  // Without a template there is no categoryBar to read, so the applicant's own
  // root-level selection is the only trustworthy source for the row category.
  const metaCategoryField = getCategoryField(null);
  const metaCategory = resolveApplicantCategory(allData, [], metaCategoryField);

  // Determine which repeatable arrays belong to which section
  const repeatableBySection = {};
  if (repeatableMeta) {
    Object.entries(repeatableMeta).forEach(([rKey, rInfo]) => {
      const sid = rInfo.sectionId;
      if (!repeatableBySection[sid]) repeatableBySection[sid] = [];
      repeatableBySection[sid].push(rKey);
    });
  }

  const sortedPages = Object.keys(pageGroups).sort((a, b) => Number(a) - Number(b));

  return sortedPages.map(pn => {
    const pg = pageGroups[pn];
    const sections = pg.sections.map(sectionKey => {
      const sectionName = (sectionNames && sectionNames[sectionKey]) || sectionKey;
      const sectionData = allData[sectionKey];
      const fields = [];
      const subSections = [];

      // Check for repeatable arrays in this section
      const repeatableKeys = repeatableBySection[sectionKey] || [];
      repeatableKeys.forEach(rKey => {
        const saved = allData[rKey] || flatData[rKey] || [];
        // Same correction as the template-driven path: the row's read-only category
        // must reflect the applicant's selection, not whatever was persisted.
        const rows = normalizeRepeatableRows(Array.isArray(saved) ? saved : [], {
          sectionName: rKey, category: metaCategory, field: metaCategoryField,
        });
        if (Array.isArray(rows) && rows.length) {
          subSections.push({
            sectionName: rKey,
            isRepeatable: true,
            rows: rows.map(rowData => ({
              _raw: rowData,
              fields: Object.entries(rowData).filter(([k]) => !k.startsWith('_')).map(([k, v]) => ({
                name: k,
                label: fieldLabels[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                value: v ?? '',
              })),
            })),
          });
        }
      });

      // Process section data (object with scalar fields)
      if (sectionData && typeof sectionData === 'object' && !Array.isArray(sectionData)) {
        Object.entries(sectionData).forEach(([k, v]) => {
          if (k.startsWith('_')) return;
          if (Array.isArray(v)) return; // handled by repeatables
          fields.push({
            name: k,
            label: fieldLabels[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            value: v ?? '',
          });
        });
      }

      return { sectionId: sectionKey, sectionName, fields, subSections };
    });

    return { pageTitle: pg.pageName, sections };
  });
};

/* ─────────────────────────────────────────────────────────
   buildFilledPagesFromApi
   ─────────────────────────────────────────────────────────
   Builds the filledPages structure expected by FinalPreview
   directly from the form template JSON + saved templateData.
   
   FULLY DYNAMIC — reads categoryBar, categoryForms, pages,
   commonPages etc. from template without hardcoding any
   specific form names, section IDs, or category values.
───────────────────────────────────────────────────────── */

// Upload fields with idNumberField/proofTypeSelect store the ID number and
// proof type as separate saved keys (`${name}_idNumber`, `${name}_proofType`)
// rather than as part of the file value itself — attach them onto the field
// object so the read-only preview can show them alongside the attachment
// (mirrors the declaration section's own mapField, which already does this for
// the signature field).
/* Read whatever was SAVED, not only what uploadConfig advertises. Gating on
   `uploadConfig.idNumberField` meant a template that collected an ID number
   without declaring that flag had it silently dropped from the preview — the
   applicant typed it, and the record did not show it. If the key
   holds nothing, nothing is attached and nothing is displayed. */
const withUploadMeta = (f, value, ...dataSources) => {
  const out = { ...f, value };
  if (f.type === 'upload' || f.type === 'file') {
    const pick = keys => {
      for (const key of keys) for (const d of dataSources) {
        if (d && d[key] != null && d[key] !== '') return d[key];
      }
      return '';
    };
    const proof = pick([`${f.name}_proofType`, `${f.name}_proof_type`]);
    const idNum = pick([`${f.name}_idNumber`, `${f.name}_id_number`]);
    if (proof) out.proofType = proof;
    if (idNum) out.idNumber = idNum;
  }
  return out;
};

export const buildFilledPagesFromApi = (templateJson, savedData) => {
  if (!savedData) return [];

  /* ── Step 1: Extract all saved field values into a flat map ── */
  let allData = {};
  let metaPageMap = null;
  let metaFieldLabels = null;
  let metaSectionNames = null;
  let metaRepeatableMeta = null;

  const extractMeta = (ALL) => {
    if (ALL._pageMap && !metaPageMap) metaPageMap = ALL._pageMap;
    if (ALL._fieldLabels && !metaFieldLabels) metaFieldLabels = ALL._fieldLabels;
    if (ALL._sectionNames && !metaSectionNames) metaSectionNames = ALL._sectionNames;
    if (ALL._repeatableMeta && !metaRepeatableMeta) metaRepeatableMeta = ALL._repeatableMeta;
  };

  if (Array.isArray(savedData)) {
    savedData.forEach(sec => {
      const td = parseTemplateDataObject(sec?.templateData);
      if (td?.ALL) {
        const ALL = td.ALL;
        extractMeta(ALL);
        if (ALL.formData && typeof ALL.formData === 'object') {
          Object.assign(allData, ALL.formData);
        }
        Object.entries(ALL).forEach(([k, v]) => {
          if (k === 'formData' || k === '_sectionNames' || k === '_fieldLabels' || k === '_pageMap' || k === '_repeatableMeta') return;
          if (k === 'repeatableData' || k === 'toggleData') return;
          allData[k] = v;
        });
      } else if (td) {
        Object.assign(allData, td);
      }
    });
  } else if (savedData?.ALL) {
    const ALL = savedData.ALL;
    extractMeta(ALL);
    if (ALL.formData && typeof ALL.formData === 'object') {
      Object.assign(allData, ALL.formData);
    }
    Object.entries(ALL).forEach(([k, v]) => {
      if (k === 'formData' || k === '_sectionNames' || k === '_fieldLabels' || k === '_pageMap' || k === '_repeatableMeta') return;
      if (k === 'repeatableData' || k === 'toggleData') return;
      allData[k] = v;
    });
  } else if (typeof savedData === 'object') {
    allData = { ...savedData };
  }

  /* ── Fallback: If no template provided, reconstruct pages from embedded metadata ── */
  if (!templateJson || (typeof templateJson === 'object' && !templateJson.pages && !templateJson.categoryForms)) {
    if (metaPageMap && metaFieldLabels) {
      return buildFilledPagesFromMetadata(allData, metaPageMap, metaFieldLabels, metaSectionNames, metaRepeatableMeta);
    }
    // No template and no metadata — cannot render
    return [];
  }

  /* ── Step 2: Build a flat lookup of all scalar values ── */
  const flatData = {};
  Object.entries(allData).forEach(([key, val]) => {
    if (key === '_sectionNames' || key === '_fieldLabels' || key === '_pageMap' || key === '_repeatableMeta' || key === 'repeatableData' || key === 'toggleData') return;
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      Object.assign(flatData, val);
    } else if (!Array.isArray(val)) {
      flatData[key] = val;
    }
  });
  Object.entries(allData).forEach(([key, val]) => {
    if (Array.isArray(val)) flatData[key] = val;
  });

  /* ── Step 3: Determine form pages from template (DYNAMIC) ── */
  let pages = null;
  // Read category field name from template's categoryBar config (dynamic, not hardcoded)
  const categoryField = getCategoryField(templateJson);
  const categoryOptions = getCategoryOptions(templateJson);
  let resolvedCategory = '';

  if (templateJson.categoryForms) {
    const availableCategories = Object.keys(templateJson.categoryForms);

    // Try to find category value from saved data. The value saved at the ROOT is the
    // one the applicant selected on the category bar (and paid against); a repeatable
    // row's read-only copy is only a last resort, and is cross-checked against the
    // section it belongs to so a mis-defaulted row cannot pick the wrong form.
    let category = flatData[categoryField] || allData[categoryField]
      || resolveApplicantCategory(allData, categoryOptions, categoryField) || '';

    // Dynamic category resolution — check which categoryForm keys have matching repeatable data
    if (!category || !availableCategories.includes(category)) {
      // Walk all categoryForms and find which has repeatable sectionNames that exist in allData
      let bestMatch = null;
      let bestScore = 0;
      for (const catKey of availableCategories) {
        const catPages = templateJson.categoryForms[catKey]?.pages || [];
        let score = 0;
        catPages.forEach(pg => {
          (pg.sections || []).forEach(sec => {
            // Check if section data exists (try sectionName first, then sectionId for backward compat)
            const sid = sec.sectionName || sec.sectionId;
            if (sid && allData[sid]) score++;
            else if (sec.sectionId && allData[sec.sectionId]) score++;
          });
        });
        // Also check for repeatable section names in this category
        const walkForRepeatables = (fields) => {
          (fields || []).forEach(f => {
            if (f.type === 'repeatable' && f.sectionName && allData[f.sectionName]) score += 2;
            if (f.sectionType === 'subSection' && f.fields) walkForRepeatables(f.fields);
          });
        };
        catPages.forEach(pg => (pg.sections || []).forEach(sec => walkForRepeatables(sec.fields)));
        if (score > bestScore) { bestScore = score; bestMatch = catKey; }
      }
      if (bestMatch) category = bestMatch;
    }

    // Final fallback: use category value directly, or first available category
    if (!category || !availableCategories.includes(category)) {
      category = availableCategories[0] || '';
    }

    resolvedCategory = category;
    pages = templateJson.categoryForms[category]?.pages;
  }

  // Case B: Direct pages (any template with pages array directly)
  if (!pages && templateJson.pages) {
    pages = templateJson.pages;
  }

  if (!pages || !pages.length) return [];

  /* ── Step 4: Resolve $ref sections (commonPages) — DYNAMIC ── */
  const resolveRef = (sections) => {
    return (sections || []).flatMap(sec => {
      if (sec.$ref) {
        const parts = sec.$ref.split('.');
        let resolved = templateJson;
        for (const p of parts) resolved = resolved?.[p];
        return Array.isArray(resolved) ? resolved : [resolved].filter(Boolean);
      }
      return [sec];
    });
  };

  /* ── Step 4b: Find every repeatable's saved rows, ONCE, for the whole form ──
     Rows are persisted under a section's DISPLAY NAME, which carries a serial the
     authority renumbers and prose it rewords — so a bare `allData[sectionName]`
     lookup lost every entry of every already-submitted application the moment
     Form E's sections were renumbered. utils/repeatableData states the resolution
     strategy; doing it for the whole form at once is what lets a confident match
     settle before any weaker one is considered. */
  // Blocks that only apply to some answers carry their rule — utils/blockVisibility.
  const resolvedPages = pages.map(page => ({
    ...page,
    sections: resolveRef(page.sections).map(sec => Array.isArray(sec?.fields) ? { ...sec, fields: withBlockRules(sec.fields) } : sec),
  }));
  const repeatableDefs = collectRepeatableDefs(resolvedPages);
  const repeatableRows = resolveRepeatableRows(repeatableDefs, allData, {
    flatSources: [flatData, ...Object.values(allData).filter(v => v && typeof v === 'object' && !Array.isArray(v))],
  });

  /* What THIS template calls "Name of the applicant", found by label. Without it
     the applicant name was looked for under a fixed list of guessed keys, and a
     form that names the field anything else — Form E's Page 1 "Name of the
     applicant (Depositor)" — resolved to no name at all. */
  const nameKeys = applicantNameKeys(resolvedPages);

  /* ── Step 5: Build filledPages from template + data ── */
  const filledPages = resolvedPages.map(page => {
    const sections = (page.sections || []).map(sec => {
      /* Structural, not name-based: a form that numbers its sections ("Part 4")
         and only says "Declaration" on the PAGE was not recognised here, so its
         undertaking text was never built and the preview showed a bare
         Place / Date / Signature. See utils/undertaking. */
      const isDecl = isDeclarationSection(sec);
      const isBmc = sec.sectionType === 'hierarchicalRepeatable' ||
        (sec.fields || []).some(f => f.type === 'hierarchicalRepeatable' || (f.name && /^(sbb|bmc)/i.test(f.name)));

      /* Declaration section */
      if (isDecl) {
        // Merge flat + section-scoped data so we can read leaf values, the
        // fill-in-the-blank answers (__declblank_*) and per-block fields.
        const sData = { ...flatData, ...(allData[sec.sectionName] || {}), ...(allData[sec.sectionId] || {}) };

        const subGroups = (sec.fields || []).filter(f => f && f.sectionType === 'subSection' && Array.isArray(f.fields));
        const isNestedDecl = subGroups.length > 0;

        const blankKey = (subIdx, paraIdx, blankIdx) => `__declblank_${sec.sectionId || 'decl'}_${subIdx}_${paraIdx}_${blankIdx}`;

        /* WHO SIGNED. The live form derives the declaration's Name from the
           applicant name(s) on Page 1 (utils/signatories) and hands the preview
           a `signatories` list. Reading a SUBMITTED application back rebuilt
           everything except that, so every form whose declaration states its
           signatory inside the undertaking sentence — Form E — filed a document
           reading "Name : N/A". The same derivation, from the saved data. */
        /* `ownDeclarationSignature` is what makes applicant 1's signature come
           back: the serialiser saves it under the declaration's OWN signature
           field, not under the live form's `declarationSignature` key. */
        /* The signatories are read from the SAME rows the read-back shows on
           Page 1 — this template's own sections, re-homed (repeatableRows) —
           never every array in the record: a saved record can also carry arrays
           under section names the form no longer uses, and each copy of an
           applicant read as another signatory ("A B, A B"). */
        const signatories = buildSignatories(
          sData, Object.fromEntries(repeatableRows), undefined, nameKeys,
          ownDeclarationSignature(sec, sData),
        );

        /* The blank that NAMES the signatory ("I/We ______ undertake that …").
           The applicant name from this same submission wins over whatever text
           is stored against it: the two are the same fact, the form now keeps
           them identical, and where an older submission holds something else —
           a half-typed name entered before Page 1 was filled in — the filed
           document must not go on saying a different person gave the
           undertaking than the one it names on Page 1. */
        const sigBlank = declSignatoryBlankKey(sec);
        const signatoryText = signatories.map(s => String(s.name || '').trim()).filter(Boolean).join(', ');

        const fillBlanks = (text, subIdx, paraIdx) => {
          let bi = 0;
          return String(text).replace(/_{3,}/g, () => {
            const key = blankKey(subIdx, paraIdx, bi); bi++;
            const v = (key === sigBlank && signatoryText) ? signatoryText : sData[key];
            return (v != null && String(v).trim()) ? String(v) : '__________';
          });
        };
        const mapField = f => withUploadMeta(f, sData[f.name] ?? '', sData);

        // Nested declarations (FORM 13): rebuild per-block heading + paragraphs
        // (blanks filled) + that block's own fields, so the read-only view renders
        // dynamically just like the live preview.
        if (isNestedDecl) {
          const declBlocks = subGroups.map((g, si) => ({
            heading: g.sectionName || '',
            paragraphs: (g.declarationText || []).map((t, pi) => fillBlanks(t, si, pi)),
            fields: (g.fields || []).filter(f => f && f.name).map(mapField),
          }));
          return {
            ...sec,
            fields: [],
            declBlocks,
            declarationAgreed: sData.declarationAgreed ?? true,
            declarationText: subGroups.flatMap((g, si) =>
              [g.sectionName, ...((g.declarationText || []).map((t, pi) => fillBlanks(t, si, pi)))].filter(Boolean)),
            agreementText: sec.agreementText || '',
          };
        }

        /* WHICH undertakings were accepted, and HOW each asked to be.
           Resolved HERE, where the template still has its blanks and the saved
           values are in hand — the preview sees text whose blanks
           already hold the applicant's words and could not work it out. An
           application saved before per-statement answers existed carries one
           blanket `declarationAgreed`, which stands in for all of them so its
           filed document does not show unticked promises. */
        const legacyAgreed = !!(sData.declarationAgreed ?? true)
          && !checkboxStatements(sec).some(st => sData[st.key] !== undefined);
        const acknowledgements = Object.fromEntries(
          checkboxStatements(sec).map(st => [st.key, isStatementAcknowledged(sData, st.key, legacyAgreed)]));
        const undertakings = resolveUndertakings(sec, { ...sData, declarationAgreed: legacyAgreed || sData.declarationAgreed })
          .map(u => ({ ...u, text: fillBlanks(u.text, u.subIdx, u.paraIdx) }));

        // Flat declaration
        /* The Name row follows Page 1 unless the applicant typed their own: an
           auto-filled value saved stale ("A B, A B" for one applicant) is read
           back as the names this submission actually lists. See
           utils/signatories.declarationNameFor. */
        const signatoryNames = signatories.map(s => s.name);
        const mapDeclField = f => (isDeclarationNameField(f)
          ? withUploadMeta(f, declarationNameFor(sData[f.name] ?? '', signatoryNames), sData)
          : mapField(f));
        return {
          ...sec,
          fields: (sec.fields || []).map(mapDeclField),
          signatories,
          undertakings,
          acknowledgements,
          /* sData is passed WHOLE, not just the tick answers: a blank-bearing
             undertaking is accepted by its blank being filled, and that value
             lives in sData. Passing only the ticks reported a fully completed
             declaration as unaccepted. */
          declarationAgreed: declarationAccepted(sec, {
            ...sData, declarationAgreed: legacyAgreed || sData.declarationAgreed, ...acknowledgements,
          }),
          declarationText: (sec.declarationText && sec.declarationText.length)
            ? (sec.declarationText || []).map((t, pi) => fillBlanks(t, 0, pi))
            : (sec.agreementText || []),
          agreementText: sec.agreementText || '',
          signature: sData.declarationSignature || sData.signature || '',
        };
      }

      /* BMC / hierarchical repeatable section */
      if (isBmc) {
        const groups = allData.groups || flatData.groups || [];
        let bmcRows = [];

        if (Array.isArray(groups) && groups.length) {
          // Standard groups format from live form editing
          bmcRows = groups.flatMap(g =>
            (g.entries || []).map(e => ({
              fields: [
                { name: 'State', label: 'State', value: g.state || '' },
                { name: 'District', label: 'District', value: g.district || '' },
                { name: 'BMC', label: 'BMC', value: e.bmcName || '' },
                { name: 'BMC_CODE', label: 'BMC Code', value: e.bmcIdentifier || e.bmcCode || '' },
                { name: 'Plants', label: 'Plants', value: (e.plantRows || []).map(r => `${r.plantName || ''}—${r.quantity || 0} ${r.quantityUnit || 'Kg'}`).join(', ') },
              ],
            }))
          );
        }

        // Fallback: check bmc-plants-summary from API saved data
        if (!bmcRows.length) {
          const summary = flatData['bmc-plants-summary'] || allData['bmc-plants-summary']
            || (allData.s2 && allData.s2['bmc-plants-summary']) || [];
          if (Array.isArray(summary) && summary.length) {
            bmcRows = summary.map(item => ({
              fields: [
                { name: 'State', label: 'State', value: item.State || item.state || '' },
                { name: 'District', label: 'District', value: item.District || item.district || '' },
                { name: 'BMC', label: 'BMC', value: item.BMC || item['BMC / LBC Name'] || item.bmcName || '' },
                { name: 'BMC_CODE', label: 'BMC Code', value: item.BMC_CODE || item.bmcCode || item.bmcLgdCode || '' },
                { name: 'Plants', label: 'Plants', value: item.Plants || item.plants || '' },
              ],
            }));
          }
        }

        return {
          ...sec,
          fields: [],
          subSections: [{
            sectionId: sec.sectionId || 'bmc-summary',
            sectionName: sec.sectionName || 'BMC & Plants Summary',
            isRepeatable: true,
            rows: bmcRows,
          }],
        };
      }

      /* Standard section — fully dynamic field walking */
      const fields = [];
      const subSections = [];

      // Rows saved with a stale/mis-defaulted "(i) Category of the applicant" are
      // corrected on the way to the screen, so an already-submitted
      // application shows the category its applicant actually chose and paid for.
      const rowsOf = rk => normalizeRepeatableRows(
        repeatableRows.get(rk) || [],
        { sectionName: rk, category: resolvedCategory, options: categoryOptions, field: categoryField }
      );

      /* TEMPLATE ORDER. A section's plain fields and its sub-sections are handed
         to the preview in two separate arrays, and the preview used to guess
         their combined order from leading serials — which fails the moment a
         form mixes "(a)…(k)" with "1.", "4.", "6." as Form E does. Every item
         emitted below is stamped with the position it held in the template, so
         the renderers restore the real order instead of guessing at it. */
      let order = 0;
      const pushField = f => { fields.push({ ...f, _order: order++ }); };
      const pushSub = sub => { subSections.push({ ...sub, _order: order++ }); };

      const readValue = (name, sd) => sd[name] ?? flatData[name] ?? '';

      const isRepeatableDef = d => d && (d.type === 'repeatable' || (d.sectionType === 'subSection' && d.type === 'repeatable'));

      const pushRepeatable = f => {
        const rk = f.sectionName || 'default';
        pushSub({
          sectionName: rk,
          isRepeatable: true,
          fieldDefs: f.fields || [],
          rows: rowsOf(rk).map(rowData => ({
            _raw: rowData,
            fields: flattenFields(f.fields || [], rowData),
          })),
        });
      };

      /* ONE BLOCK'S FIELDS, AT EVERY DEPTH.
         A sub-section's children are not always leaves: Form E's Institution
         page 1 collects everything under "3. Complete address with contact
         details and details of authorized representative", whose only children
         are the blocks "(i) Complete address …" and "(ii) Details of authorized
         representative …". Keeping only the children that carry a `name` — as
         this did — therefore kept NOTHING: the applicant's whole address, the
         entire authorized representative, the address proof and its ID number
         and the authorization letter, twenty-four answered fields, were absent
         from the submitted-application summary while sitting in the payload
         the whole time.

         A block nested inside keeps its heading as a `sectionTitle` marker and
         its own fields follow it — the same shape the live pre-submission
         preview builds (DynamicFormRenderer's walkSubSectionFields), so what
         the applicant approved is what the record shows. A repeatable is
         skipped here because it gets a block of its own. */
      const collectSubFields = (defs, sectionData, out = []) => {
        (defs || []).forEach(d => {
          if (!d) return;
          if (isRepeatableDef(d)) return;
          if (d.sectionType === 'subSection' && Array.isArray(d.fields)) {
            const heading = d.sectionName || d.label || '';
            if (heading) out.push({ sectionTitle: heading });
            /* Numbered under their own block, which is how the form itself
               numbers them: under "(i) Complete address …" the applicant fills
               in "(A) Address Line 1", so that is what the summary must call
               it back. See utils/labelNumbering. */
            collectSubFields(withNestedLabels(heading, d.fields), sectionData, out);
            return;
          }
          if (d.type === 'conditionalGroup' && d.conditions) {
            const heading = branchGroupLabel(d);
            const branch = activeBranchFields(d, name => readValue(name, sectionData));
            if (!branch.length) return;
            if (heading) out.push({ sectionTitle: heading });
            // Numbered under the block — see utils/labelNumbering.
            collectSubFields(withNestedLabels(heading, branch), sectionData, out);
            return;
          }
          if (d.name) out.push(withUploadMeta(d, readValue(d.name, sectionData), sectionData, flatData));
        });
        return out;
      };

      /* A repeatable nested at ANY depth is its own block. The old check looked
         only at a sub-section's DIRECT children, so a repeatable one level
         further in was rendered nowhere at all. */
      const pushNestedRepeatables = (defs, sectionData) => {
        (defs || []).forEach(d => {
          if (!d) return;
          if (isRepeatableDef(d)) { pushRepeatable(d); return; }
          if (d.sectionType === 'subSection' && d.fields) { pushNestedRepeatables(d.fields, sectionData); return; }
          if (d.type === 'conditionalGroup' && d.conditions) {
            pushNestedRepeatables(activeBranchFields(d, name => readValue(name, sectionData)), sectionData);
          }
        });
      };

      const processFields = (fieldList, sectionData) => {
        (fieldList || []).forEach(f => {
          if (!f) return;
          if (isRepeatableDef(f)) {
            pushRepeatable(f);
            return;
          }
          if (f.sectionType === 'subSection') {
            if (f.toggleField) {
              const tv = sectionData[f.toggleField.name] ?? flatData[f.toggleField.name] ?? f.toggleField.default ?? 'No';
              const shown = tv === toggleOpenValue(f.toggleField) ? (f.fields || []) : [];
              pushSub({
                sectionName: f.sectionName || '',
                isRepeatable: false,
                fields: [
                  { name: f.toggleField.name, label: f.sectionName || f.toggleField.name, value: tv },
                  // Numbered under the block — see utils/labelNumbering.
                  ...collectSubFields(withNestedLabels(f.sectionName || f.label, shown), sectionData),
                ],
              });
              pushNestedRepeatables(shown, sectionData);
              return;
            }
            // Regular subSection — its own leaves and every block nested inside.
            // Numbered under the block — see utils/labelNumbering.
            const sf = collectSubFields(withNestedLabels(f.sectionName || f.label, f.fields || []), sectionData);
            if (sf.length) {
              pushSub({ sectionName: f.sectionName || '', isRepeatable: false, fields: sf });
            }
            pushNestedRepeatables(f.fields || [], sectionData);
            return;
          }
          /* A conditionalGroup is a NAMED block ("(5) Geographical location of
             the biological resource"), not a bag of loose fields. Flattening its
             branch straight into the section's flat list threw the heading away
             AND moved the block to wherever flat fields happen to print — which
             is how Form E's geographical location arrived above the microorganism
             it describes, with nothing to say what those rows were.

             The branch itself is matched tolerantly (utils/conditionalBranch):
             an exact-string lookup dropped the whole block whenever the saved
             source value differed from the branch key by casing or spacing. */
          if (f.type === 'conditionalGroup' && f.conditions) {
            const heading = branchGroupLabel(f);
            // Numbered under the block — see utils/labelNumbering.
            const condFields = withNestedLabels(heading, activeBranchFields(f, name => sectionData[name] ?? flatData[name] ?? ''));
            if (!condFields.length) return;
            const nested = condFields.filter(cf => cf && cf.name && cf.sectionType !== 'subSection' && cf.type !== 'repeatable' && cf.type !== 'conditionalGroup')
              .map(cf => withUploadMeta(cf, readValue(cf.name, sectionData), sectionData, flatData));
            if (nested.length) {
              pushSub({ sectionName: heading, isRepeatable: false, fields: nested });
            }
            // Anything structural inside the branch keeps its own block.
            processFields(
              condFields.filter(cf => cf && (cf.sectionType === 'subSection' || cf.type === 'repeatable' || cf.type === 'conditionalGroup')),
              sectionData,
            );
            return;
          }
          if (f.name) {
            pushField(withUploadMeta(f, readValue(f.name, sectionData), sectionData, flatData));
          }
        });
      };

      /* A page's answers are saved under the section's DISPLAY NAME ("Part 1"),
         while the template identifies the section by its id ("part1"). Looking
         only under the id always missed, and every value was then read from the
         whole-form flat bag — which holds one entry per field NAME, so two
         sections that both collect "city" saw the same answer. Try the id, the
         display name, and the name the payload records for that id. */
      const named = metaSectionNames && metaSectionNames[sec.sectionId];
      const scoped = [sec.sectionId, sec.sectionName, named]
        .map(k => (k ? allData[k] : null))
        .find(v => v && typeof v === 'object' && !Array.isArray(v));
      const sectionData = scoped ? { ...flatData, ...scoped } : flatData;
      processFields(sec.fields, sectionData);

      return { ...sec, fields, subSections };
    });
    return { pageNumber: page.pageNumber, pageName: page.pageName, sections };
  });

  /* ── Step 6: Fallback — if template-based build returned empty, use _fieldLabels or flat key-value ── */
  if (filledPages.every(p => (p.sections || []).every(s =>
    !(s.fields || []).length && !(s.subSections || []).length && !s.declarationText
  ))) {
    // Try to use _fieldLabels metadata from saved data for proper labels
    const labels = (savedData?.ALL || savedData)?._fieldLabels || {};
    const fallbackFields = [];
    Object.entries(flatData).forEach(([key, val]) => {
      if (key === 'declarationAgreed' || key === 'declarationSignature') return;
      if (typeof val === 'object') return;
      const label = labels[key] || key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^\w/, c => c.toUpperCase());
      fallbackFields.push({ name: key, label, value: val ?? '' });
    });
    if (fallbackFields.length) {
      return [{
        pageNumber: 1,
        pageName: 'Application Data',
        sections: [{ sectionName: 'Submitted Details', fields: fallbackFields, subSections: [] }],
      }];
    }
  }

  return filledPages;
};

function flattenFields(fieldDefs, rowData) {
  const out = [];
  const walk = (fields) => {
    (fields || []).forEach(f => {
      /* These rows print FLAT, so a nested block's own (a)(b)(c) run would land
         beside the entry's own — the children carry their parent's number with
         them instead. See utils/labelNumbering. */
      if (f.sectionType === 'subSection' && f.fields) { walk(withNestedLabels(f.sectionName || f.label, f.fields)); return; }
      // Tolerant branch match — an exact lookup here dropped a repeatable
      // entry's conditional fields whenever the saved answer differed from the
      // branch key by casing or spacing. See utils/conditionalBranch.
      if (f.type === 'conditionalGroup' && f.conditions) { walk(withNestedLabels(branchGroupLabel(f), activeBranchFields(f, rowData))); return; }
      if (f.name) out.push(withUploadMeta(f, rowData[f.name] ?? '', rowData));
    });
  };
  walk(fieldDefs);
  return out;
}

/* ─────────────────────────────────────────────────────────
   buildEditState — open draft for editing
   FIX: Uses correct parseSectionsToFormData, no stripping
───────────────────────────────────────────────────────── */
export const buildEditState = async submission => {
  const appId  = submission?.id;
  const formId = submission?.formId;

  const raw = await getApplicationData(appId);

  // Parse prefill — keep all data including repeatable arrays
  const prefillData = parseSectionsToFormData(raw);

  // Extract documents from API response for upload field resolution
  const documents = extractDocuments(raw, appId);

  return {
    applicationId:   appId,
    formId,
    editSubmission:  { ...submission, application: { id: appId }, formId },
    savedSections:   raw,
    prefillData,
    documents,
    startPage:       submission.currentPage || 0,
    readOnly:        false,
    disableBmcDetails: true,
    blockedSections: [2],
    fixedApplicationId: appId,
  };
};

/* ─────────────────────────────────────────────────────────
   buildViewState — open for read-only view
───────────────────────────────────────────────────────── */
export const buildViewState = async submission => {
  const appId  = submission?.id;
  const formId = submission?.formId;

  const raw = await getApplicationData(appId);

  const prefillData = parseSectionsToFormData(raw);

  // Extract documents from API response for upload field resolution
  const documents = extractDocuments(raw, appId);

  return {
    applicationId:  appId,
    formId,
    viewSubmission: { ...submission, application: { id: appId }, formId },
    savedSections:  raw,
    prefillData,
    documents,
    readOnly:       true,
    fixedApplicationId: appId,
  };
};

